import React, { useState } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { COLORS } from "../../assets/theme";

// Stand-ins the pages fall back to when a venue has no banner; they look broken
// stretched to a cover, so treat them as "no image".
const PLACEHOLDERS = new Set(["", "/favicon.ico", "/logo192.png"]);

const initialsOf = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("") || "?";

// Stable hue per name so each venue keeps its own colour.
const hueOf = (name: string) =>
  name.split("").reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 210);

interface VenueCoverProps {
  src?: string;
  name: string;
  sx?: SxProps<Theme>;
  /** Font size of the initials when there is no image. */
  initialsSize?: string | number;
}

const VenueCover: React.FC<VenueCoverProps> = ({
  src,
  name,
  sx,
  initialsSize = "2.2rem",
}) => {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const url = (src || "").trim();
  const showImage = !PLACEHOLDERS.has(url) && failedSrc !== url;
  const hue = hueOf(name || "?");

  return (
    <Box
      sx={[
        {
          position: "relative",
          overflow: "hidden",
          bgcolor: COLORS.textFieldBackgroundColor,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {showImage ? (
        <Box
          component="img"
          className="venue-cover-img"
          src={url}
          alt=""
          loading="lazy"
          onError={() => setFailedSrc(url)}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.5s cubic-bezier(0.2, 0, 0, 1)",
          }}
        />
      ) : (
        <Box
          className="venue-cover-img"
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(255, 255, 255, 0.95)",
            fontWeight: 800,
            fontSize: initialsSize,
            letterSpacing: "0.04em",
            background: `linear-gradient(135deg, hsl(${hue}, 70%, 55%), hsl(${
              (hue + 40) % 360
            }, 70%, 42%))`,
            transition: "transform 0.5s cubic-bezier(0.2, 0, 0, 1)",
          }}
        >
          {initialsOf(name || "")}
        </Box>
      )}
    </Box>
  );
};

export default VenueCover;
